import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { useNavigation, useRouter } from 'expo-router'
import React, { useCallback } from 'react'
import { Dimensions, StyleProp, Text, TouchableOpacity, View, ViewStyle } from 'react-native'
import { ms, ScaledSheet } from 'react-native-size-matters'
import { FormButton } from '~components/FormButton'
import ModalScreen, { getModalStyleDefinition, ModalFooter } from '~components/ModalScreen'
import MyClassesList from '~components/MyClassesList'
import { useTheme } from '~theme/ThemeManager'
import { ClassId } from '~types'
import { actions, useNewChatContext } from './state'

export default function FolderLayout() {
  const styles = useStyles()
  const [state, stateDispatch] = useNewChatContext()
  const navigation = useNavigation()
  const router = useRouter()

  const selectFolder = (folder: ClassId) => {
    stateDispatch(actions.setSelectedClassId(folder))
  }

  const confirm = async () => {
    if (state.selected?.id) {
      stateDispatch(actions.setClassId(state.selected))
      router.back()
    }
  }

  const backgroundContainerStyle = useCallback(
    (folder: ClassId): StyleProp<ViewStyle> => {
      return state.selected?.id === folder.id ? styles.backgroundContainerStyle : undefined
    },
    [state.selected?.id, styles.backgroundContainerStyle],
  )

  const textContainerStyle = useCallback(
    (folder: ClassId): StyleProp<ViewStyle> => {
      return state.selected?.id === folder.id ? styles.textContainerStyle : undefined
    },
    [state.selected?.id, styles.textContainerStyle],
  )

  const dismiss = React.useCallback(() => {
    stateDispatch(actions.reset())
  }, [stateDispatch])

  return (
    <ModalScreen
      onDismiss={dismiss}
      dismissTo={state.dismissTo}
      Footer={
        <ModalFooter>
          <FormButton
            onPress={router.back}
            title={'Back'}
            style={styles.backButton}
            textStyle={styles.backButtonText}
            disabled={state.loading}
          />
          <FormButton
            onPress={confirm}
            title={'Confirm'}
            style={styles.button}
            disabled={state.selected?.id === undefined}
          />
        </ModalFooter>
      }
    >
      <View style={styles.inner}>
        <View style={styles.header}>
          <Text style={styles.subtitle}>
            Please select a folder to this chat to OR create a new folder.
          </Text>
          <TouchableOpacity onPress={navigation.goBack}>
            <FontAwesomeIcon icon={['fal', 'times-circle']} size={ms(24)} />
          </TouchableOpacity>
        </View>
        <MyClassesList
          onFolderSelected={selectFolder}
          backgroundContainerStyle={backgroundContainerStyle}
          textContainerStyle={textContainerStyle}
          join={false}
          create="chat"
          actions={false}
        />
      </View>
    </ModalScreen>
  )
}

const useStyles = () => {
  const { theme, mode } = useTheme()
  return ScaledSheet.create({
    ...getModalStyleDefinition(theme, mode),
    listItem: {
      marginVertical: '5@mvs',
      padding: '5@ms',
      backgroundColor: theme.white,
    },
    listItemSelected: {
      backgroundColor: theme.pink[200],
    },
    layoutsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    backgroundContainerStyle: {
      borderColor: theme.primary,
    },
    textContainerStyle: {
      borderColor: theme.primary,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignContent: 'center',
      alignItems: 'center',
      paddingTop: '10@mvs0.8',
      paddingStart: '15@ms',
      paddingHorizontal: '10@mvs0.8',
    },
    folderInput: {
      color: theme.primary,
    },
    sourceContainer: {
      backgroundColor: theme.white,
      borderWidth: 1,
      borderColor: theme[300],
      borderRadius: '5@ms',
      marginHorizontal: '10@ms',
      width: Dimensions.get('window').width > 640 ? '90@ms' : undefined,
      flexDirection: Dimensions.get('window').width > 640 ? 'column' : 'row',
      padding: Dimensions.get('window').width > 640 ? '15@ms' : '10@ms',
      alignItems: 'center',
    },
    sourceIcon: {
      marginTop: Dimensions.get('window').width > 640 ? '10@ms' : undefined,
      marginBottom: Dimensions.get('window').width > 640 ? '20@ms' : undefined,
      marginRight: Dimensions.get('window').width > 640 ? undefined : '10@ms',
    },
    sourceText: {
      fontSize: '10@ms',
      textAlign: 'center',
      fontWeight: '700',
      color: theme[800],
    },
    column: {
      justifyContent: 'center',
    },
    list: {
      alignSelf: 'center',
    },
    listVerticalSeparator: {
      height: Dimensions.get('window').width > 640 ? '20@mvs' : '10@mvs',
    },
  })
}
